import { useEffect } from "react";

/**
 * Global hotkey that toggles smart-view (camera-operator) motion: **⌃⌥F**.
 *
 * Capture-phase + `e.code`-matched on purpose:
 *  - capture fires before the editor's own key handling (Monaco doesn't stop
 *    propagation of unbound chords, but capturing is the safe belt);
 *  - `e.code === "KeyF"` dodges macOS Option-composition — with ⌥ held, `e.key`
 *    is the composed glyph "ƒ", so a `key`-based match would never fire.
 *
 * Registered only while `available` (the operator surface is on and not forced),
 * so read-only embeds and the forced marketing demo never bind it. `toggle`
 * must be stable (memoize at the call site) — the listener re-registers when it
 * changes.
 */
export function useSmartViewHotkey(available: boolean, toggle: () => void): void {
  useEffect(() => {
    if (!available) return;
    const onKey = (e: KeyboardEvent): void => {
      if (
        e.code === "KeyF" &&
        e.ctrlKey &&
        e.altKey &&
        !e.metaKey &&
        !e.shiftKey
      ) {
        e.preventDefault();
        e.stopPropagation();
        toggle();
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [available, toggle]);
}
